import { type ReactElement, useEffect, useRef, useState } from "react";
import {
  AdminGrid,
  AdminPageHeader,
  Button,
  Card,
  CardHeader,
  CTABand,
  CompareGrid,
  Container,
  DisclaimerCard,
  FormField,
  IconAlert,
  IconBell,
  IconMessage,
  IconMapPin,
  InlineAlert,
  PageHeader,
  PostureBar,
  PostureCell,
  PostureHead,
  PriceLockup,
  ReliabilityGrid,
  SectionHead,
  SectionHeader,
  Select,
  ProgressBar,
  Stepper,
  DashboardQuadrantPreview,
  PhonePairPreview,
} from "@constructorfleet/ui/design-system";
import { uaCoverageStats, uaOfferingResearchDataset } from "./research";
import { uaTranslations } from "./i18n";
import uaOfferingCatalog from "./offering-catalog";
import AdminStatesManager from "./AdminStatesManager";
import { UaDemoDashboard, UaLiveDashboard } from "./ua-dashboard";
import { ActiveCallsPanel } from "./admin-settings/active-calls-panel";
import { CallHistoryPanel } from "./admin-settings/call-history-panel";
import { NextCallCard } from "./admin-settings/next-call-card";
import { TriggerCallCard } from "./admin-settings/trigger-call-card";
import { RuntimeSettingsForm } from "./admin-settings/runtime-settings-form";

function InlineList({ items }: { items: readonly string[] }) {
  return (
    <ul style={{ margin: 0, paddingLeft: "1.25rem" }}>
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

const uaProductRoutes = {
  signup: `products/${uaOfferingCatalog.id}/signup`,
  demo: `products/${uaOfferingCatalog.id}/demo`,
} as const;

const baseMonthlyPriceCents = uaOfferingCatalog.billing.pricing?.price ?? 1500;

const monthlyPrice = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
}).format(baseMonthlyPriceCents / 100);

export function UaProductPage(): ReactElement {
  return (
    <main aria-label={uaTranslations.productTitle}>
      {/* ── 1. Product hero ──────────────────────────────────── */}
      <section className="phero phero-right-glow">
        <Container>
          <div className="phero-grid">
            <div>
              <span className="tag">UA Notifications</span>
              <h1 className="display" style={{ marginTop: 20 }}>
                Stop building your day around the call-in line.
              </h1>
              <p className="lede">
                We check the right line for your jurisdiction every day and text you the result —
                before your reporting window closes.
              </p>

              <PriceLockup
                amount={monthlyPrice}
                period="/mo"
                caption="Flat rate · No hidden fees · Cancel anytime"
              />

              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 8 }}>
                <Button as="a" href="/login" variant="brand" size="lg">
                  Start Setup
                </Button>
                <Button as="a" href={`/${uaProductRoutes.signup}`} variant="secondary" size="lg">
                  Check Coverage
                </Button>
              </div>
            </div>

            <div className="phero-visual">
              <DashboardQuadrantPreview>
                <img
                  src="/co-ua-notification-snapshot.png"
                  alt="UA Notifications dashboard snapshot"
                  style={{ display: "block", width: "75%", height: "auto" }}
                />
              </DashboardQuadrantPreview>
            </div>
          </div>
        </Container>
      </section>

      {/* ── 2. Disclaimer ────────────────────────────────────── */}
      <section className="disc">
        <Container>
          <DisclaimerCard icon={<IconAlert />}>
            This tool checks official state call-in lines on your behalf. You are still responsible
            for confirming coverage in your specific jurisdiction. We log every attempt and result
            for your records, but third-party logs may not be accepted as proof in all courts.
          </DisclaimerCard>
        </Container>
      </section>

      {/* ── 3. What you get vs. what's at risk ──────────────── */}
      <section className="compare">
        <Container>
          <SectionHead
            align="center"
            kicker="What's at stake"
            title="What you get vs. what's at risk."
            brand
          />
          <CompareGrid
            good={{
              label: "With",
              title: "What you actually get",
              items: [
                {
                  title: "Daily line checks, done for you",
                  desc: "We call the line at the right time and keep trying until a result is confirmed.",
                },
                {
                  title: "A text with your result",
                  desc: "You'll get a clear message telling you exactly what you need to do today.",
                },
                {
                  title: "A record you can download",
                  desc: "Every check is timestamped and saved. You can pull it up or export it anytime.",
                },
              ],
            }}
            bad={{
              label: "Without",
              title: "What's at stake without it",
              items: [
                {
                  title: "Forgetting to call",
                  desc: "Life gets busy. Missing the window even once can have real consequences.",
                },
                {
                  title: "Busy or down lines",
                  desc: "If the line is busy when you try, you might not get another chance before the deadline.",
                },
                {
                  title: "No proof you called",
                  desc: "If there's ever a dispute, you have nothing to show except your word.",
                },
              ],
            }}
          />
        </Container>
      </section>

      {/* ── 4. How it works ──────────────────────────────────── */}
      <section className="how">
        <Container>
          <SectionHead
            align="center"
            kicker="How it works"
            title="Three steps. Set once, runs forever."
            brand
          />
          <Stepper
            steps={[
              {
                title: "Choose your state",
                desc: "Tell us where you're located and what program you're in.",
              },
              {
                title: "We check the line",
                desc: "Each day, we call the right number for your jurisdiction and get your status.",
              },
              {
                title: "Get your result by text",
                desc: "You'll receive a clear message telling you whether you need to report, plus a log in your dashboard.",
              },
            ]}
          />
        </Container>
      </section>

      {/* ── 5. Records ───────────────────────────────────────── */}
      <section className="records">
        <Container>
          <SectionHead
            align="center"
            kicker="Your records"
            title="Every check, logged and exportable"
            lede="Your dashboard keeps a complete record of every check-in — date, time, result, and status. If you need to confirm what happened on a specific day, it's already here."
            brand
          />
          <PhonePairPreview deepScrollPx={0} includePhoneFrame={false}>
            <img
              src="/co-ua-notification-phone-frame1.png"
              alt="UA Notifications phone preview one"
              style={{ display: "block", width: "100%", height: "100%" }}
            />
            <img
              src="/co-ua-notification-phone-frame2.png"
              alt="UA Notifications phone preview two"
              style={{ display: "block", width: "100%", height: "100%" }}
            />
          </PhonePairPreview>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Button as="a" href={`/${uaProductRoutes.demo}`} variant="primary" size="md">
              View demo dashboard
            </Button>
          </div>
        </Container>
      </section>

      {/* ── 6. Reliability (dark) ────────────────────────────── */}
      <section className="baddays">
        <Container>
          <SectionHead
            align="center"
            kicker="Reliability"
            title="What happens if something goes wrong"
            lede="The call-in line doesn't always cooperate. Here's what happens when it doesn't."
          />
          <ReliabilityGrid
            items={[
              {
                icon: <IconBell />,
                title: "You're notified before the check",
                desc: "You get a heads-up before the line is called so you know it's happening. No guessing, no surprises.",
              },
              {
                icon: <IconMessage />,
                title: "You're notified after the check",
                desc: "As soon as the check completes, you get your result. If something went wrong, you'll know before your window closes.",
              },
              {
                icon: <IconMapPin />,
                title: "Jurisdiction changes",
                desc: "If a line number or schedule changes, we update routing as soon as we confirm the new information. Your check-in doesn't silently break.",
              },
            ]}
          />
        </Container>
      </section>

      {/* ── 7. Final CTA ─────────────────────────────────────── */}
      <section className="cta-final">
        <Container>
          <CTABand
            title="Ready to stop worrying about the daily call?"
            ctas={[
              { label: "Start Setup", href: "/login", variant: "brand" },
              {
                label: "Check Coverage",
                href: `/${uaProductRoutes.signup}`,
                variant: "secondary",
              },
            ]}
          />
        </Container>
      </section>
    </main>
  );
}

export function UaDashboardPage(): ReactElement {
  return <UaLiveDashboard />;
}

export function UaDemoDashboardPage(): ReactElement {
  return (
    <UaDemoDashboard
      signupLink={
        <Button as="a" href={`/${uaProductRoutes.signup}`} size="sm" variant="brand">
          Get started with UA Notifications
        </Button>
      }
    />
  );
}

export function UaSignupPage(): ReactElement {
  return (
    <div className="ds-stack" style={{ gap: "1rem" }}>
      <PageHeader
        title="UA Notifications"
        sub="Set up your daily call-in check in a few minutes."
      />

      <Card>
        <CardHeader
          title="What you'll need"
          meta="Have these ready before you start — it only takes a minute."
        />
        <div className="card-body ds-stack">
          <InlineList
            items={[
              "Your participant ID (for Sentry-style daily call lines)",
              "Your assigned color (for Jefferson and 18th JD diversion-style programs)",
              "Your program or jurisdiction so we can find the right call line",
            ]}
          />
        </div>
      </Card>

      <Card>
        <CardHeader
          title="Good to know"
          meta="Some call-in lines may require additional verification that we haven't fully documented yet."
        />
        <div className="card-body ds-stack">
          <InlineList items={uaOfferingResearchDataset.researchGaps.map((gap) => gap.summary)} />
        </div>
      </Card>
    </div>
  );
}

export type UaProductDbClient = {
  requestAdmin?: <TValue = unknown>(input: {
    method: "GET" | "POST" | "DELETE";
    path: string;
    body?: unknown;
  }) => Promise<{ ok: true; data: TValue } | { ok: false; error: string }>;
};

/** @deprecated Use UaProductDbClient instead */
export type CoUaProductDbClient = UaProductDbClient;

function formatCoverageStatusLabel(status: "research" | "pilot" | "live") {
  return status === "research" ? "Research" : status === "pilot" ? "Pilot" : "Live";
}

function usePostureClock() {
  const [now, setNow] = useState(() => new Date());
  const ref = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    ref.current = setInterval(() => setNow(new Date()), 1000);
    return () => {
      if (ref.current) clearInterval(ref.current);
    };
  }, []);
  const utc = now.toISOString().slice(11, 19) + " UTC";
  let tz: string;
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    tz = "Local";
  }
  const local =
    now.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    }) +
    " " +
    tz;
  return `${utc} · ${local}`;
}

export function UaAdminPage({
  dbClient,
  isAdmin,
}: {
  dbClient?: UaProductDbClient;
  isAdmin?: boolean;
} = {}): ReactElement {
  const clockSub = usePostureClock();
  const requestAdmin = dbClient?.requestAdmin;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [coverageStatus, setCoverageStatus] = useState<"research" | "pilot" | "live">("research");
  const [statesCount, setStatesCount] = useState<number | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!requestAdmin) {
        setLoading(false);
        return;
      }

      const resp = await requestAdmin<{ settings?: unknown }>({
        method: "GET",
        path: "/api/admin/co-ua/settings",
      });
      if (!mounted) return;
      if (!resp.ok) {
        setError(resp.error);
      } else if (resp.data?.settings && typeof resp.data.settings === "object") {
        const s = (resp.data as any).settings;
        if (
          s.coverageStatus === "pilot" ||
          s.coverageStatus === "live" ||
          s.coverageStatus === "research"
        ) {
          setCoverageStatus(s.coverageStatus);
        }
        if (Array.isArray(s.supportedStates)) {
          setStatesCount(s.supportedStates.length);
        }
      }
      setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, [requestAdmin]);

  async function handleSave() {
    if (!requestAdmin) return;
    setSaving(true);
    setMessage(null);
    setError(null);
    const resp = await requestAdmin({
      method: "POST",
      path: "/api/admin/co-ua/settings",
      body: { coverageStatus },
    });
    if (!resp.ok) {
      setError(resp.error);
    } else {
      setMessage("Settings saved.");
    }
    setSaving(false);
  }

  return (
    <div className="page">
      <AdminPageHeader
        title="UA Notifications Admin"
        description="Monitor coverage posture, manage supported states, and adjust configuration."
      />

      <PostureBar>
        <PostureHead
          label="Runtime posture"
          title="UA Notifications"
          sub={
            <>
              <span>{loading ? "Loading…" : formatCoverageStatusLabel(coverageStatus)}</span>
              <br />
              <span
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 10,
                  letterSpacing: "0.02em",
                }}
              >
                {clockSub}
              </span>
            </>
          }
        />
        <PostureCell
          label="Coverage"
          value={loading ? "…" : formatCoverageStatusLabel(coverageStatus)}
          dot={coverageStatus === "live" ? "ok" : coverageStatus === "pilot" ? "warn" : "mute"}
        />
        <PostureCell label="Call lines" value={uaCoverageStats.phoneLineCount} sub="Documented" />
        <PostureCell
          label="Programs"
          value={uaCoverageStats.monitoredProgramCount}
          sub="Monitored"
        />
        <PostureCell
          label="Supported states"
          value={statesCount != null ? statesCount : "—"}
          dot={statesCount != null ? "ok" : "mute"}
          sub={statesCount != null ? "Configured" : ""}
        />
        {dbClient ? <NextCallCard dbClient={dbClient} /> : null}
      </PostureBar>

      {dbClient ? (
        <>
          <SectionHeader
            title="Live Operations"
            description="Watch active traffic, reconcile stale calls, and audit recent outcomes."
          />
          <AdminGrid cols={2}>
            <ActiveCallsPanel dbClient={dbClient} />
            <TriggerCallCard dbClient={dbClient} />
          </AdminGrid>
          <CallHistoryPanel dbClient={dbClient} />
        </>
      ) : null}

      <SectionHeader
        title="Configuration"
        description="Server-side settings that control coverage posture and notification behavior."
      />
      <div className="ds-stack-lg">
        <Card wide>
          <CardHeader title="Coverage Status" />
          <div className="card-body ds-stack">
            {!requestAdmin ? (
              <InlineAlert tone="info">Admin access not available in this context.</InlineAlert>
            ) : loading ? (
              <div style={{ padding: "2rem" }}>
                <ProgressBar label="Loading settings" caption="Loading UA settings…" />
              </div>
            ) : (
              <div className="ds-stack">
                {error ? <InlineAlert tone="danger">{error}</InlineAlert> : null}
                {message ? <InlineAlert tone="success">{message}</InlineAlert> : null}

                <FormField label="Coverage status" htmlFor="ua-coverage-status">
                  <Select
                    id="ua-coverage-status"
                    value={coverageStatus}
                    onChange={(e) => setCoverageStatus(e.target.value as any)}
                  >
                    <option value="research">Research</option>
                    <option value="pilot">Pilot</option>
                    <option value="live">Live</option>
                  </Select>
                </FormField>

                <div className="shell-actions">
                  <Button
                    type="button"
                    disabled={saving}
                    onClick={(e) => {
                      e.preventDefault();
                      handleSave();
                    }}
                  >
                    {saving ? "Saving…" : "Save settings"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Card>

        <AdminStatesManager requestAdmin={requestAdmin} />
      </div>

      {dbClient ? (
        <>
          <SectionHeader
            title="Configuration Baseline"
            description="Adjust routing, call safety limits, and provider credentials that define the default runtime posture."
          />
          <RuntimeSettingsForm dbClient={dbClient} />
        </>
      ) : null}

      {isAdmin ? (
        <Card wide>
          <CardHeader title="Marketing" />
          <div className="card-body">
            <Button
              type="button"
              onClick={() => {
                window.location.href = "/products/co-ua-notifications";
              }}
            >
              Open UA marketing page
            </Button>
          </div>
        </Card>
      ) : null}
    </div>
  );
}

/** @deprecated Use UaProductPage instead */
export const ColoradoUaProductPage = UaProductPage;
/** @deprecated Use UaDashboardPage instead */
export const ColoradoUaDashboardPage = UaDashboardPage;
/** @deprecated Use UaDemoDashboardPage instead */
export const ColoradoUaDemoDashboardPage = UaDemoDashboardPage;
/** @deprecated Use UaSignupPage instead */
export const ColoradoUaSignupPage = UaSignupPage;
/** @deprecated Use UaAdminPage instead */
export const ColoradoUaAdminPage = UaAdminPage;
